import type { Identifier } from 'dnd-core';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import { Dashboard2ColumnType } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';

import useReportStore from '../../store/ReportStore';
import {
  MAX_COLUMNS_IN_ROW,
  type ReportDnDColumnItemT,
  type ReportMappedColumnT,
} from '../../types';

type ReportColumnDropzoneProps = {
  rowId: string;
  columns: ReportMappedColumnT[];
  zoneIndex: number;
};

const ReportColumnDropzone = (props: ReportColumnDropzoneProps) => {
  const moveColumn = useReportStore((state) => state.moveColumn);
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const color = useColor();
  const addToast = useToast();

  const [{ isOver, draggingColumnAlias, draggingColumnRowAlias }, dropZone] =
    useDrop<
      ReportDnDColumnItemT,
      void,
      {
        isOver: boolean;
        draggingColumnAlias: string;
        draggingColumnRowAlias: string;
      }
    >({
      accept: [
        String(Dashboard2ColumnType.DASHBOARD_COLUMN_CHART),
        String(Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX),
        String(Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS),
      ],
      collect(monitor) {
        return {
          isOver:
            (monitor.getItemType() ===
              String(Dashboard2ColumnType.DASHBOARD_COLUMN_CHART) ||
              monitor.getItemType() ===
                String(Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX) ||
              monitor.getItemType() ===
                String(Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS)) &&
            monitor.isOver(),
          draggingColumnRowAlias: monitor.getItem()?.rowId,
          draggingColumnAlias: monitor.getItem()?.column?.alias,
        };
      },
      drop(item, monitor) {
        if (
          monitor.getItemType() !==
            String(Dashboard2ColumnType.DASHBOARD_COLUMN_CHART) &&
          monitor.getItemType() !==
            String(Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX) &&
          monitor.getItemType() !==
            String(Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS)
        ) {
          return;
        }

        const type = monitor.getItemType();
        onReportDnDItemDrop(item, type);
      },
    });

  const onReportDnDItemDrop = async (
    item: ReportDnDColumnItemT,
    type: Identifier | null,
  ) => {
    try {
      const columnIds = props.columns.map((d) => d.alias);
      if (
        props.columns.length >= MAX_COLUMNS_IN_ROW &&
        columnIds.indexOf(draggingColumnAlias) === -1
      ) {
        addToast(
          `Can't add more than ${MAX_COLUMNS_IN_ROW} charts in a row.`,
          'fail',
        );
        return;
      }

      let zoneIndex = props.zoneIndex;

      // 같은 row에서 이동하는 경우
      if (draggingColumnRowAlias === props.rowId) {
        if (
          columnIds.indexOf(draggingColumnAlias) === zoneIndex ||
          columnIds.indexOf(draggingColumnAlias) === zoneIndex - 1
        ) {
          return;
        }

        // 이동할 위치가 현재 위치보다 뒤에 있는 경우
        if (columnIds.indexOf(draggingColumnAlias) < zoneIndex) {
          zoneIndex -= 1;
        }
      }

      moveColumn(draggingColumnRowAlias, item.column, props.rowId, zoneIndex);
    } catch (e) {
      setPopup({
        popupTheme: 'negative',
        title: 'Error',
        message: 'Failed to move the chart. Please try again later.',
        type: 'alert',
      });
    }
  };

  const getDropzoneWidth = (index: number) => {
    const columnLength = props.columns.length;

    if (index === 0 || index === columnLength) {
      return `calc(100% / ${columnLength} )`;
    }

    return `calc(100% / ${columnLength} * 2)`;
  };

  const showDropzone = () => {
    const columnIds = props.columns.map((d) => d.alias);
    // 같은 row에서 이동하는 경우 인접한 dropzone은 보여주지 않는다.
    if (draggingColumnRowAlias === props.rowId) {
      return (
        isOver &&
        props.zoneIndex !== columnIds.indexOf(draggingColumnAlias) &&
        props.zoneIndex !== columnIds.indexOf(draggingColumnAlias) + 1
      );
    }

    return isOver;
  };

  if (props.zoneIndex === 0) {
    return (
      <StyledDropzone
        ref={dropZone}
        showDropzone={showDropzone()}
        width={getDropzoneWidth(props.zoneIndex)}
        style={{ borderLeft: `4px solid ${color.primary200}` }}
      />
    );
  }

  if (props.zoneIndex === props.columns.length) {
    return (
      <StyledDropzone
        ref={dropZone}
        showDropzone={showDropzone()}
        width={getDropzoneWidth(props.zoneIndex)}
        style={{ borderRight: `4px solid ${color.primary200}` }}
      />
    );
  }

  return (
    <StyledDropzone
      ref={dropZone}
      showDropzone={showDropzone()}
      width={getDropzoneWidth(props.zoneIndex)}
    >
      <div
        style={{ flex: 1, borderRight: `4px solid ${color.primary200}` }}
      ></div>
      <div style={{ flex: 1 }}></div>
    </StyledDropzone>
  );
};

const StyledDropzone = styled(Flex)<{
  width: string;
  showDropzone: boolean;
}>`
  height: '100%';
  width: ${(props) => props.width};
  opacity: ${(props) => (props.showDropzone ? 1 : 0)};
  transition: opacity 0.2s;
`;

export default ReportColumnDropzone;
